/**
 * Partners that sell hellohousing subscriptions in their own shops (#2357), e.g. Haus & Grund.
 *
 * Their branding (name, logo) lives here in the frontend rather than coming from the backend's
 * partner table. Plans sold by a partner carry the partner's id as `subscriptionType.partnerId`
 * (null for hellohousing's own plans), and so does a partner's voucher.
 *
 * An entry is found by one of its `ids` (partner.id; dev and production each generate their own)
 * or, when the id isn't listed, by `subscriptionKeyPrefix`: every Haus & Grund plan's key starts
 * with HUG_ (HUG_STARTER_10_2026_ANNUAL, ...). Subscription assignments carry that key, so the
 * signed-in app finds the partner either way. The voucher check on the redeem page only returns
 * `partnerId`, so the redeem page shows the partner's branding only once its id is listed here.
 *
 * Adding a partner: an entry here, plus its logos (see assets/images/partners/hausUndGrund.js).
 */
import { HAUS_UND_GRUND_LOGO, HAUS_UND_GRUND_PDF_LOGO } from '../assets/images/partners/hausUndGrund';
import { SUBSCRIPTION_TIER_NAMES, getSubscriptionTier } from './subscriptionFeatureAccess';

export const PARTNERS = [
  {
    key: 'HAUS_UND_GRUND',
    name: 'Haus & Grund',
    // partner.id on staging (dev.hellohousing.de), as GET auth-api/vouchers/{id}/subscription answers it.
    // TODO(2357): add production's id once the backend has created the row there.
    ids: ['234be8e5-30cd-4831-2c14-08df30d56690'],
    subscriptionKeyPrefix: 'HUG_',
    // Transparent, for the sidebar and the redeem page.
    logo: HAUS_UND_GRUND_LOGO,
    // Logo + "powered by hellohousing" in one image, replacing the hellohousing logo in PDFs.
    pdfLogo: HAUS_UND_GRUND_PDF_LOGO,
  },
];

// Ids are GUIDs, which the API doesn't always send in the same letter case.
const normalizeId = (id) => (id ? id.toString().trim().toLowerCase() : '');

export const getPartner = ({ partnerId, subscriptionKey } = {}, partners = PARTNERS) => {
  const id = normalizeId(partnerId);
  const partnerById = id && partners.find((partner) => partner.ids.some((partnerIdOfEntry) => normalizeId(partnerIdOfEntry) === id));
  if (partnerById) {
    return partnerById;
  }
  const key = subscriptionKey?.toString().toUpperCase();
  return (key && partners.find((partner) => partner.subscriptionKeyPrefix && key.startsWith(partner.subscriptionKeyPrefix))) || null;
};

// Partner whose branding goes with a plan, for a raw `subscriptionType` (GET /subscriptions,
// subscription assignments) as well as for the flattened currentSubscription view-model that
// Main.js builds - both name the two fields alike.
export const getSubscriptionPartner = (subscription) =>
  subscription ? getPartner({ partnerId: subscription.partnerId, subscriptionKey: subscription.subscriptionKey }) : null;

// Partner whose branding the app shows - sidebar, PDFs, company details: the one the account was
// created through (GET account-api/accounts/{id} -> partnerId, set at signup from the partner's
// voucher), also once its plan has run out; else the partner of the current plan. Null for
// hellohousing's own customers.
export const getBrandingPartner = (accountData, currentSubscription) =>
  getPartner({ partnerId: accountData?.partnerId }) || getSubscriptionPartner(currentSubscription);

// Sold by a partner rather than by hellohousing: anything with a partnerId, or a plan of a partner
// listed above when the partnerId is missing.
export const isPartnerSubscription = (subscription) =>
  Boolean(subscription?.partnerId) || getSubscriptionPartner(subscription) !== null;

// The name the app shows for a plan. A partner plan is named after the partner ("Haus & Grund
// Professional"); the app calls it by its tier ("Professional"), as it does hellohousing's own
// plans - the partner is named by its branding and in the texts. hellohousing's plans keep their
// name. Reads the raw subscription type (`name`) and the flattened view-model (`subscriptionName`).
export const getSubscriptionDisplayName = (subscription) => {
  const name = (subscription?.subscriptionName ?? subscription?.name ?? '').toString().trim();
  if (!isPartnerSubscription(subscription)) {
    return name;
  }
  const tier = getSubscriptionDisplayTier(subscription);
  if (tier) {
    return SUBSCRIPTION_TIER_NAMES[tier];
  }
  const partner = getSubscriptionPartner(subscription);
  return partner && name.startsWith(partner.name) ? name.slice(partner.name.length).trim() || name : name;
};

// Tier of a plan for its badge colour. The voucher check sends no category, but a partner plan's
// name ends in its tier ("Haus & Grund Premium").
export const getSubscriptionDisplayTier = (subscription) => {
  const tier = getSubscriptionTier(subscription);
  if (tier || !isPartnerSubscription(subscription)) {
    return tier;
  }
  const lastWord = (subscription?.subscriptionName ?? subscription?.name ?? '').toString().trim().split(/\s+/).pop().toUpperCase();
  return SUBSCRIPTION_TIER_NAMES[lastWord] ? lastWord : undefined;
};
